import classnames from 'classnames';
import Icon from '@/components/ui/Icon';

const thumbsUpTitle = 'Thumbs up';
const thumbsDownTitle = 'Thumbs down';

export type ThumbFeedbackValue = 'thumbsUp' | 'thumbsDown';

interface ThumbFeedbackButtonsProps {
  value: ThumbFeedbackValue | null;
  onChange: (value: ThumbFeedbackValue) => void | Promise<void>;
  disabled?: boolean;
  containerClassName?: string;
  buttonClassName?: string;
  disabledClassName?: string;
  dimOppositeOnlyWhenSelected?: boolean;
  showCopyButton?: boolean;
  onCopyClick?: () => void | Promise<void>;
  copyTitle?: string;
  copyAriaLabel?: string;
  copyButtonClassName?: string;
  upTitle?: string;
  downTitle?: string;
  upAriaLabel?: string;
  downAriaLabel?: string;
}

const ThumbFeedbackButtons = ({
  value,
  onChange,
  disabled = false,
  containerClassName,
  buttonClassName,
  disabledClassName = 'cursor-not-allowed opacity-50',
  dimOppositeOnlyWhenSelected = false,
  showCopyButton = false,
  onCopyClick,
  copyTitle = 'Copy response',
  copyAriaLabel = 'Copy response',
  copyButtonClassName,
  upTitle = thumbsUpTitle,
  downTitle = thumbsDownTitle,
  upAriaLabel = thumbsUpTitle,
  downAriaLabel = thumbsDownTitle
}: ThumbFeedbackButtonsProps) => {
  const isDimmed = (buttonValue: ThumbFeedbackValue) => {
    if (!disabled) return false;
    if (!dimOppositeOnlyWhenSelected) return true;
    if (!value) return true;
    return value !== buttonValue;
  };

  const handleClick = (buttonValue: ThumbFeedbackValue) => {
    if (disabled) return;
    onChange(buttonValue);
  };

  const handleCopyClick = () => {
    if (!onCopyClick) {
      return;
    }

    void onCopyClick();
  };

  return (
    <div className={containerClassName}>
      {showCopyButton && onCopyClick ? (
        <button
          type="button"
          onClick={handleCopyClick}
          title={copyTitle}
          aria-label={copyAriaLabel}
          className={classnames(
            'flex min-h-10 items-center justify-start rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black',
            copyButtonClassName
          )}
        >
          <Icon name="copy" size="large" />
        </button>
      ) : null}

      <button
        type="button"
        onClick={() => handleClick('thumbsUp')}
        disabled={disabled}
        aria-disabled={disabled}
        aria-pressed={value === 'thumbsUp'}
        title={upTitle}
        aria-label={upAriaLabel}
        className={classnames(
          'rounded-full p-2 transition-colors',
          buttonClassName,
          isDimmed('thumbsUp') && disabledClassName
        )}
      >
        <Icon name="thumbUp" size="medium" />
      </button>

      <button
        type="button"
        onClick={() => handleClick('thumbsDown')}
        disabled={disabled}
        aria-disabled={disabled}
        aria-pressed={value === 'thumbsDown'}
        title={downTitle}
        aria-label={downAriaLabel}
        className={classnames(
          'rounded-full p-2 transition-colors',
          buttonClassName,
          isDimmed('thumbsDown') && disabledClassName
        )}
      >
        <Icon name="thumbDown" size="medium" />
      </button>
    </div>
  );
};

export default ThumbFeedbackButtons;
